import React, { createContext, useContext, useReducer, useEffect, ReactNode, useRef, useMemo } from 'react';
import { User } from '../types';
import apiService from '../services/api';

const IMPERSONATION_ORIGINAL_ACCESS_KEY = 'impersonation_original_access_token';
const IMPERSONATION_ORIGINAL_REFRESH_KEY = 'impersonation_original_refresh_token';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  isImpersonating: boolean;
  originalUser: User | null;
}

type AuthAction =
  | { type: 'LOGIN_START' }
  | { type: 'LOGIN_SUCCESS'; payload: User }
  | { type: 'LOGIN_FAILURE'; payload: string }
  | { type: 'LOGOUT' }
  | { type: 'UPDATE_USER'; payload: User }
  | { type: 'CLEAR_ERROR' }
  | { type: 'AUTH_CHECK_START' }
  | { type: 'AUTH_CHECK_COMPLETE' }
  | { type: 'START_IMPERSONATION'; payload: { impersonatedUser: User; originalUser: User } }
  | { type: 'STOP_IMPERSONATION'; payload: User };

interface AuthContextType extends AuthState {
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  updateUser: (userData: Partial<User>) => Promise<void>;
  clearError: () => void;
  startImpersonation: (userId: number) => Promise<void>;
  stopImpersonation: () => Promise<void>;
}

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
  isImpersonating: false,
  originalUser: null,
};

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  console.log('[AuthContext] Reducer action:', action);
  console.log('[AuthContext] Previous state:', { 
    user: state.user?.id, 
    isAuthenticated: state.isAuthenticated, 
    isLoading: state.isLoading 
  });
  
  let newState: AuthState;
  
  switch (action.type) {
    case 'AUTH_CHECK_START':
      newState = {
        ...state,
        isLoading: true,
        error: null,
      };
      break;
    case 'AUTH_CHECK_COMPLETE':
      newState = {
        ...state,
        isLoading: false,
      };
      break;
    case 'LOGIN_START':
      newState = {
        ...state,
        isLoading: true,
        error: null,
      };
      break;
    case 'LOGIN_SUCCESS':
      if (!action.payload) {
        newState = {
          ...state,
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: 'No user returned from login.',
        };
        break;
      }
      newState = {
        ...state,
        user: action.payload,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };
      break;
    case 'LOGIN_FAILURE':
      newState = {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
      };
      break;
    case 'LOGOUT':
      newState = {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      };
      break;
    case 'UPDATE_USER':
      newState = {
        ...state,
        user: action.payload,
      };
      break;
    case 'CLEAR_ERROR':
      newState = {
        ...state,
        error: null,
      };
      break;
    case 'START_IMPERSONATION':
      newState = {
        ...state,
        user: action.payload.impersonatedUser,
        isAuthenticated: true,
        isImpersonating: true,
        originalUser: action.payload.originalUser,
        error: null,
      };
      break;
    case 'STOP_IMPERSONATION':
      newState = {
        ...state,
        user: action.payload,
        isAuthenticated: true,
        isImpersonating: false,
        originalUser: null,
        error: null,
      };
      break;
    default:
      newState = state;
  }
  
  console.log('[AuthContext] New state:', { 
    user: newState.user?.id, 
    isAuthenticated: newState.isAuthenticated, 
    isLoading: newState.isLoading 
  });
  
  return newState;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const authCheckInProgress = useRef(false);

  // Check for existing token on app load
  useEffect(() => {
    const checkAuthStatus = async () => {
      if (authCheckInProgress.current) {
        console.log('[AuthContext] Auth check already in progress, skipping...');
        return;
      }
      authCheckInProgress.current = true;
      dispatch({ type: 'AUTH_CHECK_START' });
      const token = localStorage.getItem('access_token');
      console.log('[AuthContext] Checking auth status, token:', token);
      if (token) {
        try {
          console.log('[AuthContext] Calling getCurrentUser...');
          const user = await apiService.getCurrentUser();
          if (!user) throw new Error('No user returned from getCurrentUser');
          console.log('[AuthContext] getCurrentUser result:', user);
          
          // Check if impersonating
          if (user.is_impersonating && user.original_user && user.impersonated_user) {
            dispatch({ 
              type: 'START_IMPERSONATION', 
              payload: { 
                impersonatedUser: user.impersonated_user, 
                originalUser: user.original_user 
              } 
            });
          } else {
            dispatch({ type: 'LOGIN_SUCCESS', payload: user });
          }
        } catch (error) {
          console.error('[AuthContext] getCurrentUser error:', error);
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          dispatch({ type: 'LOGOUT' });
        }
      } else {
        dispatch({ type: 'LOGOUT' });
      }
      dispatch({ type: 'AUTH_CHECK_COMPLETE' });
      authCheckInProgress.current = false;
    };
    checkAuthStatus();
  }, []); // Only run on mount

  const login = async (username: string, password: string) => {
    dispatch({ type: 'LOGIN_START' });
    try {
      const { access, refresh, user } = await apiService.login({ username, password });
      if (!user) {
        throw new Error('No user returned from login');
      }
      localStorage.setItem('access_token', access);
      localStorage.setItem('refresh_token', refresh);
      dispatch({ type: 'LOGIN_SUCCESS', payload: user });
    } catch (error: any) {
      const data = error.response?.data;
      const detail = data?.detail;
      const errorMessage =
        (typeof detail === 'string' ? detail : Array.isArray(detail) ? detail[0] : null) ||
        data?.message ||
        error.message ||
        'Login failed. Please try again.';
      dispatch({ type: 'LOGIN_FAILURE', payload: errorMessage });
      throw error;
    }
  };

  const logout = async () => {
    try {
      await apiService.logout();
    } catch (error) {
      // Even if logout API call fails, clear local storage
      console.error('Logout API call failed:', error);
    } finally {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem(IMPERSONATION_ORIGINAL_ACCESS_KEY);
      localStorage.removeItem(IMPERSONATION_ORIGINAL_REFRESH_KEY);
      dispatch({ type: 'LOGOUT' });
    }
  };

  const updateUser = async (userData: Partial<User>) => {
    try {
      const updatedUser = await apiService.updateProfile(userData);
      dispatch({ type: 'UPDATE_USER', payload: updatedUser });
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to update profile.';
      dispatch({ type: 'LOGIN_FAILURE', payload: errorMessage });
      throw error;
    }
  };

  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  const startImpersonation = async (userId: number) => {
    try {
      // Save original tokens so we can restore later (defensive)
      const currentAccess = localStorage.getItem('access_token');
      const currentRefresh = localStorage.getItem('refresh_token');
      if (currentAccess && currentRefresh) {
        localStorage.setItem(IMPERSONATION_ORIGINAL_ACCESS_KEY, currentAccess);
        localStorage.setItem(IMPERSONATION_ORIGINAL_REFRESH_KEY, currentRefresh);
      }

      const { access, refresh, impersonated_user, original_user } = await apiService.startImpersonation(userId);

      // Swap tokens to impersonated user's JWTs
      localStorage.setItem('access_token', access);
      localStorage.setItem('refresh_token', refresh);

      dispatch({ 
        type: 'START_IMPERSONATION', 
        payload: { 
          impersonatedUser: impersonated_user, 
          originalUser: original_user 
        } 
      });

      // Refresh user data (now using impersonated JWT) to ensure state aligns with token claims
      const updatedUser = await apiService.getCurrentUser();
      if (updatedUser.is_impersonating && updatedUser.original_user && updatedUser.impersonated_user) {
        dispatch({ 
          type: 'START_IMPERSONATION', 
          payload: { 
            impersonatedUser: updatedUser.impersonated_user, 
            originalUser: updatedUser.original_user 
          } 
        });
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to start impersonation';
      dispatch({ type: 'LOGIN_FAILURE', payload: errorMessage });
      throw error;
    }
  };

  const stopImpersonation = async () => {
    try {
      const { access, refresh, user } = await apiService.stopImpersonation();

      // Swap tokens back to original user's JWTs (freshly minted by backend)
      localStorage.setItem('access_token', access);
      localStorage.setItem('refresh_token', refresh);
      localStorage.removeItem(IMPERSONATION_ORIGINAL_ACCESS_KEY);
      localStorage.removeItem(IMPERSONATION_ORIGINAL_REFRESH_KEY);

      // Fetch current user under restored token
      const updatedUser = await apiService.getCurrentUser();
      dispatch({ type: 'STOP_IMPERSONATION', payload: updatedUser || user });
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to stop impersonation';
      dispatch({ type: 'LOGIN_FAILURE', payload: errorMessage });
      throw error;
    }
  };

  const value: AuthContextType = useMemo(() => ({
    ...state,
    login,
    logout,
    updateUser,
    clearError,
    startImpersonation,
    stopImpersonation,
  }), [state]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  console.log('[useAuth] Context accessed, user:', context?.user?.id, 'isAuthenticated:', context?.isAuthenticated);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 